import React, {
  MutableRefObject,
  Suspense,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  KYC_2ND_PUSH_INTERVAL,
  KYC_CHECK_INTERVAL,
  KYC_CHECK_INTERVAL_SHORT,
  KYC_HOW_MANY_MINUTES_TO_START,
  KYC_PASSED_CHECK_INTERVAL,
  KYC_PUSH_INTERVAL_MINS,
} from './constants';
import { useToast } from '../../../util/hooks/useToast';
import { useTranslation } from 'react-i18next';
import {
  useCurrentUser,
  useIsAuthenticated,
  useTraderKycStatusQuery,
  useUserKycStatusQuery,
} from '../../../apis/user/use';
import { useLazyUserQuery } from '../../../apis/user/api';
import { isFeatureOn } from 'whitelabel';
import { Features } from 'whitelabel/type';
import { useDispatch, useSelector } from 'react-redux';
import { setKycTraderInProgress, setUser } from 'apis/user/store';
import { KycResponse, KycStatus } from 'apis/user/types';
import { useZAlert } from 'components/ZModal/use';
import { differenceInMinutes, format, subMinutes } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { ROUTE_BECOME_TRADER, ROUTE_KYC } from 'routes';
import { junkyard } from 'util/junkyard';
import { usePrevious } from 'react-use';
import { appendToUrlHash } from '../../../util/vwo';
import { RootState } from '../../../apis/store';
import { useTraderServices } from '../../../apis/service/use';
import { isKycExpiredOrExpiring } from '../../../views/TraderService/components/BecomeTraderLanding/util';
import { extractStatus } from '../../../apis/user/util';

export function usePollKyc(isTrader: boolean): KycResponse | undefined {
  const isAuthenticated = useIsAuthenticated();
  const canCheckKyc = isAuthenticated && isFeatureOn(Features.Kyc);
  const { exchanges } = useCurrentUser();
  const exchangeActivated = exchanges?.some((e) => e.activated);

  const [kycPollingInterval, setKycPollingInterval] = useState(
    // we should have a longer one here
    KYC_PASSED_CHECK_INTERVAL,
  );

  const { data: traderKyc } = useTraderKycStatusQuery({
    pollingInterval: kycPollingInterval,
    skip: !canCheckKyc || !exchangeActivated || !isTrader,
  });

  const { data: userKyc } = useUserKycStatusQuery({
    pollingInterval: kycPollingInterval,
    skip: !canCheckKyc || !exchangeActivated || isTrader,
  });

  const currentKyc = userKyc || traderKyc;

  const kycInitiationDate = useSelector((state: RootState) => state.user)?.[
    isTrader ? 'traderKycInitiatedAt' : 'userKycInitiatedAt'
  ];
  const hasInitiatedKycRecently =
    new Date(kycInitiationDate) >
    subMinutes(new Date(), KYC_HOW_MANY_MINUTES_TO_START);

  useEffect(() => {
    if (extractStatus(currentKyc)) {
      setKycPollingInterval(
        // we do not want to check kyc too often if:
        //  it's already approved or rejected and there are no premises to its status changing soon
        (([KycStatus.APPROVED, KycStatus.REJECTED] as string[]).includes(
          extractStatus(currentKyc),
        ) &&
          !hasInitiatedKycRecently) ||
          // or it's a trader kyc that hasn't been requested yet
          (isTrader && !kycInitiationDate)
          ? KYC_PASSED_CHECK_INTERVAL
          : ([KycStatus.INIT, KycStatus.PENDING] as string[]).includes(
                extractStatus(currentKyc),
              )
            ? KYC_CHECK_INTERVAL_SHORT
            : KYC_CHECK_INTERVAL,
      );
    }
  }, [currentKyc, hasInitiatedKycRecently]);

  return currentKyc;
}

export function useCheckKyc(isTrader: boolean) {
  const toast = useToast();
  const [loadUser] = useLazyUserQuery();
  const dispatch = useDispatch();
  const { t } = useTranslation(['kyc', 'common']);

  const kyc = usePollKyc(isTrader);

  const previousKyc = usePrevious(kyc);

  // logic to refetch the user if the new kyc has been updated
  useEffect(() => {
    if (
      previousKyc &&
      kyc &&
      extractStatus(kyc) !== extractStatus(previousKyc) &&
      // we do not want to show the "status updated" text to someone who's just initiated the kyc
      // I know I've just updated it, duh
      !(
        [
          KycStatus.NOT_STARTED,
          KycStatus.PENDING,
          KycStatus.INIT,
        ] as KycStatus[]
      ).includes(extractStatus(kyc))
    ) {
      toast.info(
        t(
          extractStatus(kyc) === KycStatus.EXPIRED
            ? `common:${isTrader ? 'trader-' : ''}kyc-expired`
            : `common:${isTrader ? 'trader-' : ''}kyc-updated`,
        ),
      );
      if (extractStatus(kyc) === KycStatus.APPROVED) {
        appendToUrlHash(`thanks_${isTrader ? 'trader-' : ''}kyc`);
      }
      // probably completely redundant
      loadUser()
        .unwrap()
        .then((userData) => dispatch(setUser(userData)));
      if (isTrader) {
        dispatch(setKycTraderInProgress(false));
      }
    }
  }, [previousKyc, kyc]);

  return kyc;
}

function usePushExpiringKyc(
  kyc: KycResponse,
  isTrader: boolean,
  isPushOpen: MutableRefObject<boolean>,
) {
  const { data: traderServices } = useTraderServices();
  const showAlert = useZAlert();
  const { t } = useTranslation(['kyc', 'common']);
  const navigate = useNavigate();
  const delayedShowTimeout = useRef<number | null>(null);

  // logic to show the "fill your kyc or your services get locked" modal
  const showAlertPushingTraderToPassKyc = () => {
    if (!traderServices?.length) return;
    const key = isTrader ? 'kycPushedTrader' : 'kycPushed';
    const kycPushedDate = junkyard.get(key);
    const expiryData = isKycExpiredOrExpiring(kyc);

    const needsPush =
      (
        [
          KycStatus.NOT_STARTED,
          KycStatus.INIT,
          KycStatus.REJECTED_RETRY,
          KycStatus.EXPIRED,
        ] as KycStatus[]
      ).includes(extractStatus(kyc)) ||
      (isTrader &&
        // should work for regular user kyc as well
        (expiryData.expiring || expiryData.expired));

    if (
      needsPush &&
      (!kycPushedDate ||
        differenceInMinutes(new Date(), new Date(kycPushedDate)) >=
          KYC_PUSH_INTERVAL_MINS)
    ) {
      if (!isPushOpen.current) {
        isPushOpen.current = true;
        junkyard.set(key, new Date().toISOString());

        showAlert({
          title: t('service-push.title'),
          okLabel: t(!isTrader ? 'service-push.ok' : 'service-push.renew'),
          description: t(
            isTrader
              ? `service-push.description-trader-kyc-${
                  expiryData.expired
                    ? 'expired'
                    : expiryData.expiring
                      ? 'expiring'
                      : 'problematic'
                }`
              : 'service-push.description',
            {
              date: format(new Date(expiryData.kycExpiryDate), 'PP p'),
            },
          ),
          okAction: () => {
            if (isTrader) {
              navigate(ROUTE_BECOME_TRADER, { state: { openKyc: true } });
            } else {
              navigate(ROUTE_KYC);
            }
            isPushOpen.current = false;
          },
          onClose: () => {
            isPushOpen.current = false;
          },
        });
      } else {
        // means we already have another push open
        // cornercase but lets cover it
        delayedShowTimeout.current && clearTimeout(delayedShowTimeout.current);
        delayedShowTimeout.current = setTimeout(
          showAlertPushingTraderToPassKyc,
          KYC_2ND_PUSH_INTERVAL,
        );
      }
    }
  };

  // clear timeout just in case
  // this would prevent the kyc msg from being shown to logged out users
  useEffect(
    () => () => {
      delayedShowTimeout.current && clearTimeout(delayedShowTimeout.current);
    },
    [delayedShowTimeout],
  );

  // isTrader, isPushOpen, showAlert and other deps are immutable
  // but the difference since the last push mechanism protects us from spamming the alerts
  // so let's add these deps to the array here to appease the linter
  useEffect(showAlertPushingTraderToPassKyc, [
    kyc,
    traderServices,
    t,
    isTrader,
    navigate,
  ]);
}

const UserKycChecker: React.FC = () => {
  // this whole concept of passing isTrader=boolean flag is kinda ugly but this works
  const traderKyc = useCheckKyc(true);
  const userKyc = useCheckKyc(false);

  const isPushOpen = useRef(false);

  usePushExpiringKyc(userKyc, false, isPushOpen);
  usePushExpiringKyc(traderKyc, true, isPushOpen);

  return null;
};

export default () => (
  <Suspense fallback={null}>
    <UserKycChecker />
  </Suspense>
);
